import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Percent } from "lucide-react";
import { Database } from "@/types/supabase";
import { useCommissionTypes } from "@/hooks/useCommissionTypes";

type Client = Database["public"]["Tables"]["clients"]["Row"];

const clientSchema = z.object({
  name: z.string().min(1, "El nombre es requerido"),
  address: z.string().min(1, "La dirección es requerida"),
  phone: z.string().min(1, "El teléfono es requerido"),
  email: z.string().email("Correo electrónico inválido").optional().nullable(),
  rnc: z.string().min(1, "El RNC es requerido"),
  commission_type_id: z.string().min(1, "Debe seleccionar un tipo de comisión"),
});

type ClientFormData = z.infer<typeof clientSchema>;

interface ClientFormProps {
  onSubmit: (data: ClientFormData) => void;
  initialData?: Partial<Client & { commission_type_id?: string }>;
  isSubmitting?: boolean;
}

const ClientForm: React.FC<ClientFormProps> = ({
  onSubmit,
  initialData,
  isSubmitting = false,
}) => {
  const { commissionTypes, loading: loadingCommissionTypes } = useCommissionTypes();
  
  const form = useForm<ClientFormData>({
    resolver: zodResolver(clientSchema),
    defaultValues: {
      name: initialData?.name || "",
      address: initialData?.address || "",
      phone: initialData?.phone || "",
      email: initialData?.email || "",
      rnc: initialData?.rnc || "",
      commission_type_id: initialData?.commission_type_id || "",
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
        {/* Primera fila - Nombre completo */}
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nombre del cliente</FormLabel>
              <FormControl>
                <Input placeholder="Nombre completo" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Segunda fila - RNC y Teléfono */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FormField
            control={form.control}
            name="rnc"
            render={({ field }) => (
              <FormItem>
                <FormLabel>RNC</FormLabel>
                <FormControl>
                  <Input placeholder="RNC del cliente" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Teléfono</FormLabel>
                <FormControl>
                  <Input placeholder="Número de teléfono" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Tercera fila - Email y Dirección */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Correo electrónico</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="correo@ejemplo.com"
                    {...field}
                    value={field.value || ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="address"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Dirección</FormLabel>
                <FormControl>
                  <Input placeholder="Dirección completa" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Cuarta fila - Tipo de comisión */}
        <FormField
          control={form.control}
          name="commission_type_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <Percent className="h-4 w-4" />
                Tipo de Comisión
              </FormLabel>
              <Select 
                onValueChange={field.onChange} 
                defaultValue={field.value}
                disabled={loadingCommissionTypes}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona el tipo de cliente" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {commissionTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      <div className="flex items-center justify-between w-full">
                        <span className="flex-1">{type.display_name}</span>
                        <Badge variant="secondary" className="ml-2 text-xs">
                          {type.percentage}%
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription className="text-xs">
                Determina el porcentaje de comisión para este cliente
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? "Guardando..." : "Guardar cliente"}
        </Button>

        {/* Información mejorada sobre tipos de comisión */}
        <div className="mt-4 p-4 bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg border border-primary/20">
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 bg-primary/10 rounded-full">
              <Percent className="h-4 w-4 text-primary" />
            </div>
            <h4 className="font-semibold text-foreground text-sm">
              Tipos de Comisión
            </h4>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {commissionTypes.map((type) => (
              <div 
                key={type.id} 
                className="flex items-center justify-between p-2.5 bg-card rounded-md border border-border/50 hover:border-primary/30 transition-colors"
              >
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-foreground">{type.display_name}</span>
                  <span className="text-xs text-muted-foreground">Comisión</span>
                </div>
                <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20 font-semibold">
                  {type.percentage}%
                </Badge>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-3 text-center">
            💡 El tipo seleccionado determinará tu porcentaje de comisión en las ventas
          </p>
        </div>
      </form>
    </Form>
  );
};

export default ClientForm; 